import { useState, useEffect, useCallback } from 'react'
import { api } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'
import Tabs from '../components/Tabs'

interface ListeningQuery { queryID: number; displayName: string; keywords: string; excludeKeywords?: string; hashtags?: string; handles?: string; platformFilter?: string; alertOnNegative: boolean; pollIntervalMinutes: number; lastPolledAt?: string; createdAt: string }
interface Mention { mentionID: number; platformID: number; authorHandle: string; authorName?: string; authorFollowerCount?: number; content: string; contentUrl: string; sentimentID: number; sentimentScore?: number; engagementCount: number; detectedAt: string; platformPostDate?: string }

const SENTIMENT: Record<number, string> = { 1: 'Positive', 2: 'Neutral', 3: 'Negative' }
const SENTIMENT_CLASS: Record<number, string> = { 1: 'smm-badge-published', 2: '', 3: 'smm-badge-danger' }
const PLATFORMS = [
  { id: 0, name: 'All Platforms' },
  { id: 1, name: 'Instagram' }, { id: 2, name: 'Facebook' }, { id: 3, name: 'X/Twitter' },
  { id: 4, name: 'LinkedIn' }, { id: 5, name: 'TikTok' }, { id: 6, name: 'YouTube' }, { id: 8, name: 'Bluesky' },
]

interface Connector { id: string; platform: string; method: string; status: string }

const TABS = [
  { id: 'queries', label: 'Queries' },
  { id: 'mentions', label: 'Mentions' },
  { id: 'overview', label: 'Overview' },
  { id: 'connectors', label: 'Connectors' },
]

export default function Listening() {
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const [tab, setTab] = useState('queries')
  const [queries, setQueries] = useState<ListeningQuery[]>([])
  const [selectedQuery, setSelectedQuery] = useState<ListeningQuery | null>(null)
  const [mentions, setMentions] = useState<Mention[]>([])
  const [sentimentFilter, setSentimentFilter] = useState(0)
  const [platformFilters, setPlatformFilters] = useState<number[]>([])
  const [msg, setMsg] = useState('')
  const [newName, setNewName] = useState('')
  const [newKeywords, setNewKeywords] = useState('')
  const [newPlatformFilter, setNewPlatformFilter] = useState<string[]>([])
  const [newAlert, setNewAlert] = useState(false)
  const [connectors, setConnectors] = useState<Connector[]>([])

  useEffect(() => { loadQueries() }, [])
  useEffect(() => { api.get<Connector[]>(`/workspaces/${wsId}/listening/connectors`).then(setConnectors).catch(err => console.error("[SMM]", err)) }, [])

  const loadQueries = async () => {
    try { setQueries(await api.get<ListeningQuery[]>(`/workspaces/${wsId}/listening/queries`)) } catch (err) { console.error('[SMM]', err) }
  }

  const selectQuery = useCallback(async (q: ListeningQuery) => {
    setSelectedQuery(q); setTab('mentions')
    try {
      let qs = `?pageSize=100`
      if (sentimentFilter) qs += `&sentimentId=${sentimentFilter}`
      if (platformFilters.length > 0) qs += `&platformId=${platformFilters[0]}`
      const allMentions = await api.get<Mention[]>(`/workspaces/${wsId}/listening/queries/${q.queryID}/mentions${qs}`)
      setMentions(platformFilters.length > 1 ? allMentions.filter(m => platformFilters.includes(m.platformID)) : allMentions)
    } catch (err) { console.error('[SMM]', err); setMentions([]) }
  }, [sentimentFilter, platformFilters])

  const handleCreate = async () => {
    if (!newName.trim() || !newKeywords.trim()) return
    try {
      await api.post(`/workspaces/${wsId}/listening/queries`, {
        displayName: newName, keywords: newKeywords, alertOnNegative: newAlert,
        platformFilter: newPlatformFilter.length > 0 ? newPlatformFilter.join(',') : null,
      })
      setNewName(''); setNewKeywords(''); setNewPlatformFilter([]); setMsg('Query created'); loadQueries()
    } catch (err) { console.error('[SMM]', err); setMsg('Failed') }
  }

  const togglePlatformFilter = (id: number) => {
    setPlatformFilters(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id])
  }

  const toggleNewPlatform = (name: string) => {
    setNewPlatformFilter(prev => prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name])
  }

  const handleTriggerPoll = async (queryId: number) => {
    try { await api.post(`/workspaces/${wsId}/listening/queries/${queryId}/poll`); setMsg('Poll triggered') } catch (err) { console.error('[SMM]', err); setMsg('Poll failed') }
  }

  const handleDelete = async (id: number) => {
    try { await api.del(`/workspaces/${wsId}/listening/queries/${id}`); loadQueries(); setMsg('Removed') } catch (err) { console.error('[SMM]', err); setMsg('Failed') }
  }

  const sentimentCounts = mentions.reduce<Record<number, number>>((acc, m) => { acc[m.sentimentID] = (acc[m.sentimentID] || 0) + 1; return acc }, {})
  const totalMentions = mentions.length
  const sentimentMax = Math.max(...Object.values(sentimentCounts), 1)

  return (
    <div className="smm-listening">
      <h1>Social Listening</h1>
      {msg && <div className="smm-success-card">{msg}</div>}
      <Tabs tabs={TABS} activeTab={tab} onTabChange={setTab} />

      {tab === 'queries' && (
        <div className="smm-card">
          <h3>Listening Queries</h3>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <input className="smm-input" style={{ minWidth: 150 }} placeholder="Query name" value={newName} onChange={e => setNewName(e.target.value)} />
            <input className="smm-input" style={{ flex: 1, minWidth: 200 }} placeholder="Keywords (comma-separated)" value={newKeywords} onChange={e => setNewKeywords(e.target.value)} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={newAlert} onChange={e => setNewAlert(e.target.checked)} /> Alert negative
            </label>
            <button className="smm-btn smm-btn-primary" onClick={handleCreate}>Create</button>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span className="smm-hint" style={{ alignSelf: 'center', marginRight: '0.5rem' }}>Networks:</span>
            {PLATFORMS.filter(p => p.id > 0).map(p => (
              <label key={p.id} className={`smm-filter-btn ${newPlatformFilter.includes(p.name) ? 'active' : ''}`}
                style={{ cursor: 'pointer', padding: '4px 10px', borderRadius: 6 }}>
                <input type="checkbox" checked={newPlatformFilter.includes(p.name)}
                  onChange={() => toggleNewPlatform(p.name)} style={{ display: 'none' }} />
                {p.name}
              </label>
            ))}
            {newPlatformFilter.length === 0 && <span className="smm-hint" style={{ alignSelf: 'center' }}>(all platforms)</span>}
          </div>
          <table className="smm-table">
            <thead><tr><th>Name</th><th>Keywords</th><th>Platform</th><th>Alert</th><th>Last Polled</th><th></th></tr></thead>
            <tbody>
              {queries.map(q => (
                <tr key={q.queryID} onClick={() => selectQuery(q)} style={{ cursor: 'pointer' }} className={selectedQuery?.queryID === q.queryID ? 'smm-row-selected' : ''}>
                  <td><strong>{q.displayName}</strong></td><td>{q.keywords}</td>
                  <td>{q.platformFilter || 'All'}</td>
                  <td>{q.alertOnNegative ? <span className="smm-badge smm-badge-danger">On</span> : 'Off'}</td>
                  <td>{q.lastPolledAt ? new Date(q.lastPolledAt).toLocaleString() : 'Never'}</td>
                  <td style={{ display: 'flex', gap: '0.25rem' }}>
                    <button className="smm-btn smm-btn-sm" onClick={e => { e.stopPropagation(); handleTriggerPoll(q.queryID) }} title="Trigger poll now">Poll</button>
                    <button className="smm-btn smm-btn-sm smm-btn-danger" onClick={e => { e.stopPropagation(); handleDelete(q.queryID) }}>x</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {queries.length === 0 && <p className="smm-hint">No listening queries yet. Create one to start monitoring mentions.</p>}
        </div>
      )}

      {tab === 'mentions' && (
        <div className="smm-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>Mentions{selectedQuery ? `: ${selectedQuery.displayName}` : ''}</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select className="smm-select" value={sentimentFilter} onChange={e => { setSentimentFilter(Number(e.target.value)); if (selectedQuery) selectQuery(selectedQuery) }}>
                <option value={0}>All Sentiment</option><option value={1}>Positive</option><option value={2}>Neutral</option><option value={3}>Negative</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <span className="smm-hint" style={{ alignSelf: 'center', marginRight: '0.5rem' }}>Filter by network:</span>
            {PLATFORMS.filter(p => p.id > 0).map(p => (
              <label key={p.id} className={`smm-filter-btn ${platformFilters.includes(p.id) ? 'active' : ''}`}
                style={{ cursor: 'pointer', padding: '4px 10px', borderRadius: 6 }}>
                <input type="checkbox" checked={platformFilters.includes(p.id)}
                  onChange={() => { togglePlatformFilter(p.id); if (selectedQuery) setTimeout(() => selectQuery(selectedQuery!), 50) }}
                  style={{ display: 'none' }} />
                {p.name}
              </label>
            ))}
            {platformFilters.length > 0 && (
              <button className="smm-btn smm-btn-sm" onClick={() => { setPlatformFilters([]); if (selectedQuery) setTimeout(() => selectQuery(selectedQuery!), 50) }}>Clear</button>
            )}
          </div>
          {!selectedQuery && <p className="smm-hint">Select a query from the Queries tab to view mentions.</p>}
          {selectedQuery && mentions.length > 0 ? (
            <div className="smm-mention-feed">
              {mentions.map(m => (
                <div key={m.mentionID} className="smm-card smm-mention-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>@{m.authorHandle}</strong>
                    <span className={`smm-badge ${SENTIMENT_CLASS[m.sentimentID] || ''}`}>{SENTIMENT[m.sentimentID] || 'Unknown'}</span>
                  </div>
                  <p>{m.content}</p>
                  <div className="smm-hint">
                    {m.authorFollowerCount ? `${m.authorFollowerCount.toLocaleString()} followers` : ''}
                    {m.engagementCount > 0 ? ` | ${m.engagementCount} engagements` : ''}
                    {m.platformPostDate ? ` | ${new Date(m.platformPostDate).toLocaleString()}` : ''}
                    {m.contentUrl && <> | <a href={m.contentUrl} target="_blank" rel="noopener">View</a></>}
                  </div>
                </div>
              ))}
            </div>
          ) : selectedQuery ? <p className="smm-hint">No mentions found for this query.</p> : null}
        </div>
      )}

      {tab === 'overview' && (
        <div className="smm-card">
          <h3>Sentiment Overview</h3>
          {totalMentions > 0 ? (
            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              {[{ id: 1, label: 'Positive', color: '#27ae60' }, { id: 2, label: 'Neutral', color: '#6c757d' }, { id: 3, label: 'Negative', color: '#e74c3c' }].map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: 80, fontWeight: 500 }}>{s.label}</div>
                  <div style={{ flex: 1, height: 24, background: '#f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${((sentimentCounts[s.id] || 0) / sentimentMax) * 100}%`, background: s.color, borderRadius: 8 }} />
                  </div>
                  <span style={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{sentimentCounts[s.id] || 0}</span>
                </div>
              ))}
            </div>
          ) : <p className="smm-hint">Load mentions from a query to see the sentiment overview.</p>}
          <p className="smm-hint">Select a query and view mentions to populate this overview. Sentiment is computed from the active mention set.</p>
        </div>
      )}

      {tab === 'connectors' && (
        <div className="smm-card">
          <h3>Listening Connectors</h3>
          <p className="smm-hint">Each connector method is polled for mentions matching your queries. Available connectors can be enabled per-query via the platform filter.</p>
          <table className="smm-table">
            <thead><tr><th>Platform</th><th>Method</th><th>Status</th></tr></thead>
            <tbody>
              {connectors.map(c => (
                <tr key={c.id}>
                  <td><strong>{c.platform}</strong></td>
                  <td>{c.method}</td>
                  <td>
                    {c.status === 'available'
                      ? <span className="smm-badge smm-badge-published">Available</span>
                      : <span className="smm-badge">Coming Soon</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
